import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const NavBar: React.FC = () => {
  const { isAuthenticated, logout, userType } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openTrack, setOpenTrack] = useState(false);
  const [openAnalyze, setOpenAnalyze] = useState(false);
  const [openTools, setOpenTools] = useState(false);
  const [openAdmin, setOpenAdmin] = useState(false);
  const [openTrackMobile, setOpenTrackMobile] = useState(false);
  const [openAnalyzeMobile, setOpenAnalyzeMobile] = useState(false);
  const [openToolsMobile, setOpenToolsMobile] = useState(false);
  const [openAdminMobile, setOpenAdminMobile] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `text-stone-600 hover:text-emerald-600 no-underline text-lg font-medium px-4 py-2 rounded-lg transition-all duration-300 hover:bg-emerald-50/50 ${
      isActive ? 'text-emerald-600 bg-emerald-50/70 font-semibold' : ''
    }`;

  const mobileNavLinkClass = ({ isActive }: { isActive: boolean }) =>
    `mobile-nav-link block w-full text-left text-stone-600 hover:text-emerald-600 no-underline text-lg font-medium px-4 py-3 rounded-lg transition-all duration-300 hover:bg-emerald-50/50 border-b border-stone-100 ${
      isActive ? 'text-emerald-600 bg-emerald-50/70 font-semibold' : ''
    }`;

  return (
    <nav className="w-full bg-white/95 backdrop-blur-sm shadow-md border-b border-stone-200 flex justify-center h-16 sticky top-0 z-50">
      <div className="w-full max-w-7xl flex items-center px-4 md:px-8">
        <NavLink to="/" className="flex items-center mr-8 text-decoration-none" onClick={closeMobileMenu}>
          <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center text-white font-bold text-lg mr-3">
            D
          </div>
          <span className="text-xl md:text-2xl font-bold text-emerald-700">DoseWise</span>
        </NavLink>
        
        {/* Desktop Navigation Links (Grouped) */}
        <div className="hidden md:flex items-center gap-2">
          {isAuthenticated ? (
            <>
              <NavLink to="/dashboard" className={navLinkClass}>
                Dashboard
              </NavLink>
              {/* Track */}
              <div className="relative" onMouseLeave={() => setOpenTrack(false)}>
                <button
                  className="text-stone-700 hover:text-emerald-700 px-4 py-2 rounded-lg hover:bg-emerald-50/50 font-medium"
                  onClick={() => setOpenTrack(v => !v)}
                >
                  Track ▾
                </button>
                {/* pt-1 (instead of mt-1) keeps the gap inside the hover area so onMouseLeave doesn't fire between button and menu (DOS-19) */}
                {openTrack && (
                  <div className="absolute left-0 top-full z-50 w-48 pt-1">
                    <div className="bg-white border border-stone-200 rounded-lg shadow-lg py-2">
                      <NavLink to="/log-dose" className={({ isActive }) => `${isActive ? 'bg-emerald-50/70 text-emerald-700' : 'text-stone-700 hover:bg-emerald-50/50'} block px-4 py-2`} onClick={() => setOpenTrack(false)}>Log Dose</NavLink>
                      <NavLink to="/weight" className={({ isActive }) => `${isActive ? 'bg-emerald-50/70 text-emerald-700' : 'text-stone-700 hover:bg-emerald-50/50'} block px-4 py-2`} onClick={() => setOpenTrack(false)}>Weight</NavLink>
                      <NavLink to="/symptoms" className={({ isActive }) => `${isActive ? 'bg-emerald-50/70 text-emerald-700' : 'text-stone-700 hover:bg-emerald-50/50'} block px-4 py-2`} onClick={() => setOpenTrack(false)}>Symptoms</NavLink>
                    </div>
                  </div>
                )}
              </div>

              {/* Analyze */}
              <div className="relative" onMouseLeave={() => setOpenAnalyze(false)}>
                <button
                  className="text-stone-700 hover:text-emerald-700 px-4 py-2 rounded-lg hover:bg-emerald-50/50 font-medium"
                  onClick={() => setOpenAnalyze(v => !v)}
                >
                  Analyze ▾
                </button>
                {openAnalyze && (
                  <div className="absolute left-0 top-full z-50 w-56 pt-1">
                    <div className="bg-white border border-stone-200 rounded-lg shadow-lg py-2">
                      <NavLink to="/cost-savings" className={({ isActive }) => `${isActive ? 'bg-emerald-50/70 text-emerald-700' : 'text-stone-700 hover:bg-emerald-50/50'} block px-4 py-2`} onClick={() => setOpenAnalyze(false)}>Cost Savings</NavLink>
                      <NavLink to="/pk-curve" className={({ isActive }) => `${isActive ? 'bg-emerald-50/70 text-emerald-700' : 'text-stone-700 hover:bg-emerald-50/50'} block px-4 py-2`} onClick={() => setOpenAnalyze(false)}>Blood Concentration</NavLink>
                    </div>
                  </div>
                )}
              </div>

              {/* Tools */}
              <div className="relative" onMouseLeave={() => setOpenTools(false)}>
                <button
                  className="text-stone-700 hover:text-emerald-700 px-4 py-2 rounded-lg hover:bg-emerald-50/50 font-medium"
                  onClick={() => setOpenTools(v => !v)}
                >
                  Tools ▾
                </button>
                {openTools && (
                  <div className="absolute left-0 top-full z-50 w-48 pt-1">
                    <div className="bg-white border border-stone-200 rounded-lg shadow-lg py-2">
                      <NavLink to="/export" className={({ isActive }) => `${isActive ? 'bg-emerald-50/70 text-emerald-700' : 'text-stone-700 hover:bg-emerald-50/50'} block px-4 py-2`} onClick={() => setOpenTools(false)}>Export</NavLink>
                    </div>
                  </div>
                )}
              </div>

              {/* Settings */}
              <NavLink to="/settings" className={navLinkClass}>
                Settings
              </NavLink>

              {/* Admin */}
              {userType === 'admin' && (
                <div className="relative" onMouseLeave={() => setOpenAdmin(false)}>
                  <button
                    className="text-stone-700 hover:text-emerald-700 px-4 py-2 rounded-lg hover:bg-emerald-50/50 font-medium"
                    onClick={() => setOpenAdmin(v => !v)}
                  >
                    Admin ▾
                  </button>
                  {openAdmin && (
                    <div className="absolute left-0 top-full z-50 w-64 pt-1">
                      <div className="bg-white border border-stone-200 rounded-lg shadow-lg py-2">
                        <NavLink to="/admin" className={({ isActive }) => `${isActive ? 'bg-emerald-50/70 text-emerald-700' : 'text-stone-700 hover:bg-emerald-50/50'} block px-4 py-2`} onClick={() => setOpenAdmin(false)}>Dashboard</NavLink>
                        <NavLink to="/admin/pricing" className={({ isActive }) => `${isActive ? 'bg-emerald-50/70 text-emerald-700' : 'text-stone-700 hover:bg-emerald-50/50'} block px-4 py-2`} onClick={() => setOpenAdmin(false)}>Pricing</NavLink>
                        <NavLink to="/admin/users" className={({ isActive }) => `${isActive ? 'bg-emerald-50/70 text-emerald-700' : 'text-stone-700 hover:bg-emerald-50/50'} block px-4 py-2`} onClick={() => setOpenAdmin(false)}>Users</NavLink>
                        <NavLink to="/admin/email-campaigns" className={({ isActive }) => `${isActive ? 'bg-emerald-50/70 text-emerald-700' : 'text-stone-700 hover:bg-emerald-50/50'} block px-4 py-2`} onClick={() => setOpenAdmin(false)}>Email Campaigns</NavLink>
                        <NavLink to="/admin/reddit-marketing" className={({ isActive }) => `${isActive ? 'bg-emerald-50/70 text-emerald-700' : 'text-stone-700 hover:bg-emerald-50/50'} block px-4 py-2`} onClick={() => setOpenAdmin(false)}>Reddit Marketing</NavLink>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <NavLink to="/" end className={navLinkClass}>
              Home
            </NavLink>
          )}
        </div>

        {/* Spacer to push buttons to the right */}
        <div className="flex-grow" />

        {/* Right-side actions: Log Dose CTA + Mobile Menu Button + Logout */}
        {isAuthenticated && (
          <NavLink to="/log-dose" className={({ isActive }) => `hidden md:inline-block ${isActive ? '' : ''} bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-200 mr-2`}>
            Log Dose
          </NavLink>
        )}
        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <button
            onClick={toggleMobileMenu}
            className="p-2 rounded-lg text-stone-600 hover:text-emerald-600 hover:bg-emerald-50/50 transition-colors duration-200"
            aria-label="Toggle mobile menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Auth Buttons */}
        {isAuthenticated && (
          <button
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg cursor-pointer font-medium transition-colors duration-200 ml-2"
            onClick={logout}
          >
            Logout
          </button>
        )}
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <>
          {/* Mobile Menu Overlay */}
          <div className="mobile-menu-overlay md:hidden" onClick={closeMobileMenu} />
          
          {/* Mobile Menu Content */}
          <div className="md:hidden absolute top-16 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-lg border-b border-stone-200 z-40 mobile-nav-menu">
            <div className="px-4 py-2">
              {isAuthenticated ? (
                <>
                  <NavLink to="/dashboard" className={mobileNavLinkClass} onClick={closeMobileMenu}>Dashboard</NavLink>
                  {/* Keep Log Dose as top-level CTA */}
                  <NavLink to="/log-dose" className={mobileNavLinkClass} onClick={closeMobileMenu}>Log Dose</NavLink>

                  {/* Track group */}
                  <button className="w-full text-left text-stone-700 font-semibold px-4 py-3" onClick={() => setOpenTrackMobile(v => !v)}>Track {openTrackMobile ? '▴' : '▾'}</button>
                  {openTrackMobile && (
                    <div className="pl-4">
                      <NavLink to="/log-dose" className={mobileNavLinkClass} onClick={closeMobileMenu}>Log Dose</NavLink>
                      <NavLink to="/weight" className={mobileNavLinkClass} onClick={closeMobileMenu}>Weight</NavLink>
                      <NavLink to="/symptoms" className={mobileNavLinkClass} onClick={closeMobileMenu}>Symptoms</NavLink>
                    </div>
                  )}

                  {/* Analyze group */}
                  <button className="w-full text-left text-stone-700 font-semibold px-4 py-3" onClick={() => setOpenAnalyzeMobile(v => !v)}>Analyze {openAnalyzeMobile ? '▴' : '▾'}</button>
                  {openAnalyzeMobile && (
                    <div className="pl-4">
                      <NavLink to="/cost-savings" className={mobileNavLinkClass} onClick={closeMobileMenu}>Cost Savings</NavLink>
                      <NavLink to="/pk-curve" className={mobileNavLinkClass} onClick={closeMobileMenu}>Blood Concentration</NavLink>
                    </div>
                  )}

                  {/* Tools group */}
                  <button className="w-full text-left text-stone-700 font-semibold px-4 py-3" onClick={() => setOpenToolsMobile(v => !v)}>Tools {openToolsMobile ? '▴' : '▾'}</button>
                  {openToolsMobile && (
                    <div className="pl-4">
                      <NavLink to="/export" className={mobileNavLinkClass} onClick={closeMobileMenu}>Export</NavLink>
                    </div>
                  )}

                  {/* Admin group */}
                  {userType === 'admin' && (
                    <>
                      <button className="w-full text-left text-stone-700 font-semibold px-4 py-3" onClick={() => setOpenAdminMobile(v => !v)}>Admin {openAdminMobile ? '▴' : '▾'}</button>
                      {openAdminMobile && (
                        <div className="pl-4">
                          <NavLink to="/admin" className={mobileNavLinkClass} onClick={closeMobileMenu}>Dashboard</NavLink>
                          <NavLink to="/admin/pricing" className={mobileNavLinkClass} onClick={closeMobileMenu}>Pricing</NavLink>
                          <NavLink to="/admin/users" className={mobileNavLinkClass} onClick={closeMobileMenu}>Users</NavLink>
                          <NavLink to="/admin/email-campaigns" className={mobileNavLinkClass} onClick={closeMobileMenu}>Email Campaigns</NavLink>
                          <NavLink to="/admin/reddit-marketing" className={mobileNavLinkClass} onClick={closeMobileMenu}>Reddit Marketing</NavLink>
                        </div>
                      )}
                    </>
                  )}

                  {/* Settings */}
                  <NavLink to="/settings" className={mobileNavLinkClass} onClick={closeMobileMenu}>Settings</NavLink>
                </>
              ) : (
                <NavLink to="/" end className={mobileNavLinkClass} onClick={closeMobileMenu}>
                  Home
                </NavLink>
              )}
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default NavBar;